import { Card, CardContent } from "@/shared/components/ui/card";
import { useBlogPosts } from "@/shared/hooks/useBlogPosts";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { useMemo } from "react";
import { FaFacebook, FaEnvelope, FaLinkedin } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

interface RelatedArticlesWidgetProps {
  currentSlug: string;
}

export default function RelatedArticlesWidget({ currentSlug }: RelatedArticlesWidgetProps) {
  const { data, isLoading } = useBlogPosts(10, 0);

  // Filter out current article and take first 3
  const relatedArticles = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter((post) => post.fieldData.slug !== currentSlug).slice(0, 3);
  }, [data?.items, currentSlug]);

  // Get the article title for sharing
  const currentArticle = useMemo(() => {
    if (!data?.items) return null;
    return data.items.find((post) => post.fieldData.slug === currentSlug);
  }, [data?.items, currentSlug]);

  const shareUrl = `https://www.elayne.com/resources/${currentSlug}`;
  const shareTitle = currentArticle?.fieldData.name || "Check out this article";

  if (isLoading) {
    return (
      <div>
        <h3 className="text-base font-semibold text-primary mb-4">Related Articles</h3>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
        </div>
      </div>
    );
  }

  if (relatedArticles.length === 0) {
    return null;
  }

  return (
    <div>
      <h3 className="text-base font-semibold text-primary mb-4">Related Articles</h3>
      <div className="flex flex-col gap-3">
        {relatedArticles.map((post) => (
          <Link
            key={post.id}
            to={POST_LOSS_ROUTES.RESOURCES_ARTICLE.replace(":slug", post.fieldData.slug)}
            className="block no-underline"
          >
            <Card className="border-slate-200/60 bg-white hover:bg-stone-50 transition-colors cursor-pointer shadow-none">
              <CardContent className="p-4">
                <p className="text-sm font-medium text-primary mb-1">{post.fieldData.name}</p>
                {post.fieldData["primary-category-data"]?.name && (
                  <p className="text-sm text-stone-500">
                    {post.fieldData["primary-category-data"].name}
                  </p>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
      <h3 className="text-base font-semibold text-primary mb-4 mt-[24px]">Share this story</h3>
      <div className="flex items-center gap-4">
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#1777f2]"
        >
          <FaFacebook className="w-6 h-6" />
        </a>
        <a
          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
            shareTitle,
          )}&url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-black"
        >
          <FaXTwitter className="w-6 h-6" />
        </a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            shareUrl,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0474b3]"
        >
          <FaLinkedin className="w-6 h-6" />
        </a>
        <a
          href={`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(
            `I thought you might find this article helpful: ${shareTitle}\n\n${shareUrl}`,
          )}`}
          className="text-stone-600 hover:text-primary transition-colors"
        >
          <FaEnvelope className="w-6 h-6" />
        </a>
      </div>
    </div>
  );
}
